import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../../../../config";
import AdminQuiz from "./adminQuiz";
import AdminVideos from "./adminVideos";
import AdminQuestions from "./adminQuestions";
import Footer from "../../../homepage/footer";

const AdminTopics = () => {
    const [allTopics, setAllTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [currentEmbedCode, setCurrentEmbedCode] = useState("");
    const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newTopic, setNewTopic] = useState({ name: "", grade_id: "", subject_id: "" });


    // Fetch topics and group by grade
    const fetchTopics = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/topics`);

            if (response.data && Array.isArray(response.data)) {
                // Group topics by grade
                const groupedTopics = response.data.reduce((acc, topic) => {
                    const gradeKey = topic.grade_id === 8 ? "Grade 8/9" : `Grade ${topic.grade_id}`;

                    if (!acc[gradeKey]) {
                        acc[gradeKey] = { grade: gradeKey, topics: [] };
                    }
                    acc[gradeKey].topics.push(topic);
                    return acc;
                }, {});

                // Sort each grade's topics by `topic_id`
                Object.values(groupedTopics).forEach((group) => {
                    group.topics.sort((a, b) => a.id - b.id);
                });
                setAllTopics(Object.values(groupedTopics));
            } else {
                console.error("Invalid API response format", response.data);
                setAllTopics([]);
            }
        } catch (err) {
            console.error("Error fetching topics:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        fetchTopics();
    }, []);


    // Open video modal
    const handleEditVideo = (topicId, embedCode) => {
        setSelectedTopic(topicId);
        setCurrentEmbedCode(embedCode);
        setIsVideoModalOpen(true);
    };

    // Open quiz modal
    const handleManageQuiz = (topicId) => {
        setSelectedTopic(topicId);
        setIsQuizModalOpen(true);
    };

    // Open exam questions & solutions modal
    const handleEditQuestions = (topicId) => {
        setSelectedTopic(topicId);
        setIsQuestionsModalOpen(true);
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div className="text-red-500">{error}</div>;


    const handleAddTopic = async () => {
        try {
            await axios.post(`${API_BASE}/api/topics/admin/manage`, newTopic);
            fetchTopics(); // Refresh the list
            setIsAddModalOpen(false);
        } catch (err) {
            console.error("Error adding topic:", err);
        }
    };


    const handleDeleteTopic = async (topicId) => {
        if (!window.confirm("Are you sure you want to delete this topic?")) return;
        try {
            await axios.delete(`${API_BASE}/api/topics/admin/${topicId}`);
            fetchTopics(); // Refresh after deletion
        } catch (err) {
            console.error("Error deleting topic:", err);
        }
    };
    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-10 flex-grow">
                <h1 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Manage Topics</h1>
            <button
                onClick={() => setIsAddModalOpen(true)}
                className="bg-teal-500 text-white px-4 py-2 rounded-md hover:bg-teal-600 mb-4"
            >
                + Add Topic
            </button>
            {allTopics.length === 0 ? (
                <p className="text-gray-600">No topics available.</p>
            ) : (
                allTopics.map((grade, index) => (
                    <section key={index} className="mb-12">
                        {/* Grade Header */}
                        <h2 className="text-xl sm:text-2xl font-semibold text-teal-600 mb-3 sm:mb-4">{grade.grade}</h2>
                        {/* Topics Table */}
                        <div className="bg-white shadow-md rounded-lg overflow-hidden -mx-4 sm:mx-0 overflow-x-auto ios-scroll-touch">
                            <table className="table-auto w-full min-w-[800px] md:min-w-0 border-collapse text-xs sm:text-base">
                                <thead>
                                    <tr className="bg-teal-500 text-white text-left">
                                        <th className="px-3 py-2 sm:px-6 sm:py-4 whitespace-nowrap">Topic</th>
                                        <th className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">Video</th>
                                        <th className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">Quiz</th>
                                        <th className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">Exam Questions</th>
                                        <th className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">Solutions</th>
                                        <th className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(grade.topics) && grade.topics.length > 0 ? (
                                        grade.topics.map((topic, index) => (
                                            <tr
                                                key={index}
                                                className={`${index % 2 === 0 ? "bg-gray-50" : "bg-white"} hover:bg-gray-100`}
                                            >
                                                {/* Topic Name */}
                                                <td className="px-3 py-2 sm:px-6 sm:py-4 font-semibold text-gray-700">{topic.name}</td>

                                                {/* Edit Video */}
                                                <td className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleEditVideo(topic.id, topic.video_url)}
                                                        className="text-teal-600 font-medium hover:underline"
                                                    >
                                                        Edit Video
                                                    </button>
                                                </td>

                                                {/* Edit Quiz */}
                                                <td className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleManageQuiz(topic.id)}
                                                        className="text-pink-600 font-medium hover:underline"
                                                    >
                                                        Edit Quiz
                                                    </button>
                                                </td>

                                                {/* Edit Exam Questions */}
                                                <td className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleEditQuestions(topic.id)}
                                                        className="text-indigo-600 font-medium hover:underline"
                                                    >
                                                        Edit Exam Questions
                                                    </button>
                                                </td>

                                                {/* Edit Solutions */}
                                                <td className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">
                                                    <button
                                                        onClick={() => handleEditQuestions(topic.id)}
                                                        className="text-gray-600 font-medium hover:underline"
                                                    >
                                                        Edit Solutions
                                                    </button>
                                                </td>

                                                <td className="px-3 py-2 sm:px-6 sm:py-4 text-center whitespace-nowrap">
                                                    <button onClick={() => handleDeleteTopic(topic.id)} className="text-red-600 font-medium hover:underline">
                                                        Delete
                                                    </button>
                                                </td>

                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="px-4 py-2 text-center text-gray-500">
                                                No topics available for this grade.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                ))
            )}

            {/* Modals */}
            {isQuizModalOpen && (
                <AdminQuiz topicId={selectedTopic} onClose={() => setIsQuizModalOpen(false)} />
            )}
            {isVideoModalOpen && (
                <AdminVideos
                    topicId={selectedTopic}
                    currentEmbedCode={currentEmbedCode}
                    onClose={() => setIsVideoModalOpen(false)}
                />
            )}
            {isQuestionsModalOpen && (
                <AdminQuestions topicId={selectedTopic} onClose={() => setIsQuestionsModalOpen(false)} />
            )}
            {
                isAddModalOpen && (
                    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
                        <div className="bg-white p-8 rounded-xl shadow-xl w-96">
                            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Add New Topic</h2>

                            {/* Topic Name Input */}
                            <input
                                type="text"
                                placeholder="Topic Name"
                                className="w-full px-4 py-2 border rounded-lg mb-4 focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                value={newTopic.name}
                                onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })}
                            />

                            {/* Grade Dropdown */}
                            <select
                                className="w-full px-4 py-2 border rounded-lg mb-4 bg-white focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                value={newTopic.grade_id}
                                onChange={(e) => setNewTopic({ ...newTopic, grade_id: e.target.value })}
                            >
                                <option value="" disabled>Select Grade</option>
                                <option value="1">Grade 1</option>
                                <option value="2">Grade 2</option>
                                <option value="3">Grade 3</option>
                                <option value="4">Grade 4</option>
                                <option value="5">Grade 5</option>
                                <option value="6">Grade 6</option>
                                <option value="7">Grade 7</option>
                                <option value="8">Grade 8/9</option>
                            </select>

                            {/* Subject Dropdown */}
                            <select
                                className="w-full px-4 py-2 border rounded-lg mb-6 bg-white focus:ring-2 focus:ring-teal-400 focus:outline-none"
                                value={newTopic.subject_id}
                                onChange={(e) => setNewTopic({ ...newTopic, subject_id: e.target.value })}
                            >
                                <option value="" disabled>Select Subject</option>
                                <option value="1">Numbers</option>
                                <option value="2">Algebra</option>
                                <option value="3">Geometry</option>
                                <option value="4">Ratio, Proportion & Rate of Change</option>
                                <option value="5">Probability</option>
                                <option value="6">Statistics</option>
                            </select>

                            {/* Buttons */}
                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => setIsAddModalOpen(false)}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddTopic}
                                    className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            </main>
            <Footer />
        </div>
    );
};


export default AdminTopics;
